import { useEffect, useState } from "react";
import { useForm, useFieldArray, useWatch, type Control } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDolfinClient, useDolfinAuth } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { recurrenceCadenceValues, type components } from "@dolfin/api-admin";
import { enumOptions } from "@/lib/enum-options";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Spinner } from "@/components/ui/spinner";
import { Lock, Plus, Save } from "lucide-react";
import { InfoHint } from "@/components/InfoHint";
import { ErrorState, LoadingState } from "@/components/PageUi";
import { DatePicker } from "@/components/ui/date-picker";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { CreateProductDialog } from "@/components/CreateProductDialog";
import { useInvoiceResources } from "@/hooks/useInvoiceResources";
import { BackLink, PageContainer, PageHeader } from "@/components/PagePrimitives";
import {
  ScheduleLineItemRow,
  SchedulePlanSummary,
  type ScheduleLineItemsForm,
} from "@/components/CreateSchedulePage";
import {
  DiscountFields,
  computeTotals,
  discountPayload,
  discountValueIssue,
  discountExceedsIssues,
  discountTypeSchema,
  discountValueSchema,
  type DiscountType,
} from "@/components/Discount";
import { ScheduleStatusPill } from "@/components/ScheduleShared";

type ScheduleResponse = components["schemas"]["RecurringInvoiceScheduleResponse"];
type RecurrenceCadence = components["schemas"]["RecurrenceCadence"];

const CADENCES = enumOptions(recurrenceCadenceValues);

// Amber ring on edited-but-unsaved fields, as in the organisation settings form.
const DIRTY_RING = "ring-2 ring-amber-500/50";

const editScheduleSchema = z.object({
  memo: z.string().nullable(),
  paymentTermsDays: z
    .number()
    .int("Payment terms must be a whole number of days")
    .min(0, "Payment terms cannot be negative")
    .max(365, "Payment terms cannot exceed 365 days"),
  startDate: z.string().min(1, "Start date is required"),
  cadence: z.string().min(1, "Cadence is required"),
  totalOccurrences: z
    .number()
    .int("Occurrences must be a whole number")
    .min(1, "Occurrences must be at least 1")
    .nullable(),
  autoSend: z.boolean(),
  discountType: discountTypeSchema,
  discountValue: discountValueSchema,
  lineItems: z
    .array(
      z.object({
        productId: z.string().min(1, "Product is required"),
        quantity: z.number().min(0.01, "Quantity must be greater than 0"),
        taxRateId: z.string().nullable(),
        unitPriceOverride: z
          .number()
          .min(0, "Price override must not be negative")
          .nullable(),
        discountType: discountTypeSchema,
        discountValue: discountValueSchema,
      }),
    )
    .min(1, "At least one line item is required"),
})
  .superRefine((values, ctx) => {
    const headerIssue = discountValueIssue(values.discountType, values.discountValue);
    if (headerIssue)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["discountValue"], message: headerIssue });

    values.lineItems.forEach((item, index) => {
      const issue = discountValueIssue(item.discountType, item.discountValue);
      if (issue)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lineItems", index, "discountValue"],
          message: issue,
        });
    });
  });

type EditScheduleFormValues = z.infer<typeof editScheduleSchema>;

function toDateInput(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : "";
}

function toFormValues(schedule: ScheduleResponse, orgDefaultTerms: number): EditScheduleFormValues {
  // Decimal-typed fields arrive as string | number from the generated schema — coerce.
  return {
    memo: schedule.memo ?? "",
    // Legacy schedules may have no terms snapshot; fall back to the organisation default for display.
    paymentTermsDays: schedule.paymentTermsDays != null ? Number(schedule.paymentTermsDays) : orgDefaultTerms,
    startDate: toDateInput(schedule.startDate),
    cadence: schedule.cadence ?? "Monthly",
    totalOccurrences: schedule.totalOccurrences != null ? Number(schedule.totalOccurrences) : null,
    autoSend: schedule.autoSend ?? false,
    discountType: (schedule.discountType as DiscountType | undefined) ?? "None",
    discountValue: schedule.discountValue != null ? Number(schedule.discountValue) : null,
    lineItems: (schedule.lineItems ?? []).map((li) => ({
      productId: li.productId ?? "",
      quantity: Number(li.quantity ?? 1),
      taxRateId: li.taxRateId ?? null,
      unitPriceOverride: li.unitPriceOverride != null ? Number(li.unitPriceOverride) : null,
      discountType: (li.discountType as DiscountType | undefined) ?? "None",
      discountValue: li.discountValue != null ? Number(li.discountValue) : null,
    })),
  };
}

export interface EditSchedulePageProps {
  scheduleId: string;
  onSaved: (scheduleId: string) => void;
  onCancel: () => void;
}

export function EditSchedulePage({ scheduleId, onSaved, onCancel }: EditSchedulePageProps) {
  const api = useDolfinClient();
  const { organisationId } = useDolfinAuth();
  const resources = useInvoiceResources();
  const { products, taxRates, getProductPrice } = resources;
  const [schedule, setSchedule] = useState<ScheduleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const form = useForm<EditScheduleFormValues>({
    resolver: zodResolver(editScheduleSchema),
    defaultValues: {
      memo: "",
      paymentTermsDays: 30,
      startDate: "",
      cadence: "Monthly",
      totalOccurrences: null,
      autoSend: false,
      discountType: "None",
      discountValue: null,
      lineItems: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "lineItems",
  });

  const { dirtyFields, isDirty } = form.formState;
  const watchedStartDate = useWatch({ control: form.control, name: "startDate" });
  const watchedCadence = useWatch({ control: form.control, name: "cadence" });
  const watchedOccurrences = useWatch({ control: form.control, name: "totalOccurrences" });
  const watchedAutoSend = useWatch({ control: form.control, name: "autoSend" });
  const watchedLineItems = useWatch({ control: form.control, name: "lineItems" });
  const watchedDiscountType = useWatch({ control: form.control, name: "discountType" });
  const watchedDiscountValue = useWatch({ control: form.control, name: "discountValue" });

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [{ data, error: apiError }, orgArRes] = await Promise.all([
          api.GET("/v1/recurring-invoices/{id}", { params: { path: { id: scheduleId } } }),
          organisationId
            ? api.GET("/v1/organisations/{organisationId}/settings/ar", {
                params: { path: { organisationId } },
              })
            : Promise.resolve(null),
          resources.fetchAll(),
        ]);
        if (apiError || !data) {
          setError("Failed to load schedule");
          return;
        }
        const orgDefaultTerms = orgArRes?.data?.defaultPaymentTermsDays != null
          ? Number(orgArRes.data.defaultPaymentTermsDays)
          : 30;
        setSchedule(data);
        form.reset(toFormValues(data, orgDefaultTerms));
      } catch (e) {
        console.error("Failed to load schedule:", e);
        setError("Failed to load schedule");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [scheduleId]);

  // StartDate and Cadence anchor the occurrence date math; the backend rejects edits once
  // the first invoice has been generated (RecurringInvoice.AnchorLocked).
  const occurrencesGenerated = Number(schedule?.occurrencesGenerated ?? 0);
  const anchorLocked = occurrencesGenerated > 0;
  const startDateDirty = toDateInput(schedule?.startDate) !== watchedStartDate;
  const cadenceDirty = (schedule?.cadence ?? "Monthly") !== watchedCadence;

  // Line items: any per-field dirtiness, an added/removed row, or reordering counts.
  const lineItemsDirty =
    Boolean(dirtyFields.lineItems) ||
    fields.length !== (schedule?.lineItems?.length ?? 0);

  const perOccurrenceSubtotal = computeTotals(
    (watchedLineItems ?? [])
      .filter((li) => li?.productId)
      .map((li) => ({
        quantity: li.quantity || 0,
        unitPrice: li.unitPriceOverride ?? getProductPrice(li.productId),
        discountType: li.discountType,
        discountValue: li.discountValue,
        taxRatePercent: 0,
      })),
    watchedDiscountType,
    watchedDiscountValue,
  ).totalAmount;

  async function onSubmit(values: EditScheduleFormValues) {
    if (!schedule) return;

    // Same per-occurrence ceiling the server enforces on a schedule patch.
    const exceeded = discountExceedsIssues(
      computeTotals(
        values.lineItems.map((li) => ({
          quantity: li.quantity || 0,
          unitPrice: li.unitPriceOverride ?? getProductPrice(li.productId),
          discountType: li.discountType,
          discountValue: li.discountValue,
          taxRatePercent: 0,
        })),
        values.discountType,
        values.discountValue,
      ),
    );
    const firstLineIssue = exceeded.lines.findIndex((issue) => issue !== null);
    if (firstLineIssue !== -1) {
      form.setError(`lineItems.${firstLineIssue}.discountValue`, {
        message: exceeded.lines[firstLineIssue] ?? undefined,
      });
      return;
    }
    if (exceeded.invoice) {
      form.setError("discountValue", { message: exceeded.invoice });
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      // PATCH semantics: only send what changed — omitted fields stay untouched.
      const body: Record<string, unknown> = {};
      if (dirtyFields.memo) body.memo = values.memo || null;
      if (dirtyFields.paymentTermsDays) body.paymentTermsDays = values.paymentTermsDays;
      if (dirtyFields.autoSend) body.autoSend = values.autoSend;
      if (dirtyFields.totalOccurrences && values.totalOccurrences !== null)
        body.totalOccurrences = values.totalOccurrences;
      if (startDateDirty) body.startDate = `${values.startDate}T00:00:00Z`;
      if (cadenceDirty) body.cadence = values.cadence as RecurrenceCadence;
      // Sent only when touched, so an untouched edit never restates the schedule's discount. The
      // backend treats an omitted discountType as "leave unchanged" and None as "remove".
      if (dirtyFields.discountType || dirtyFields.discountValue) {
        const { discountType, discountValue } = discountPayload(
          values.discountType,
          values.discountValue,
        );
        body.discountType = discountType;
        body.discountValue = discountValue;
      }
      if (lineItemsDirty)
        body.lineItems = values.lineItems.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          taxRateId: item.taxRateId || null,
          unitPriceOverride: item.unitPriceOverride,
          ...discountPayload(item.discountType, item.discountValue),
        }));

      if (Object.keys(body).length === 0) {
        toast.info("No changes to save");
        return;
      }

      const { data, error: apiError } = await api.PATCH("/v1/recurring-invoices/{id}", {
        params: { path: { id: scheduleId } },
        body,
      });

      if (apiError || !data) {
        const message = apiErrorMessage(apiError, "Failed to save recurring invoice.");
        setSaveError(message);
        toast.error(message);
        return;
      }

      toast.success("Recurring invoice updated - changes apply to future invoices");
      onSaved(scheduleId);
    } catch (e) {
      console.error("Failed to save schedule:", e);
      setSaveError("An unexpected error occurred while saving the recurring invoice.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <PageContainer size="narrow">
        <BackLink onClick={onCancel}>Back to recurring invoice</BackLink>
        <LoadingState label="Loading recurring invoice..." />
      </PageContainer>
    );
  }

  if (error || !schedule) {
    return (
      <PageContainer size="narrow">
        <BackLink onClick={onCancel}>Back to recurring invoice</BackLink>
        <ErrorState message={error ?? "That recurring invoice could not be found."} />
      </PageContainer>
    );
  }

  const customerName = schedule.customer?.name ?? "Unknown customer";

  return (
    <PageContainer size="narrow">
      <BackLink onClick={onCancel}>Back to recurring invoice</BackLink>
      <PageHeader
        eyebrow="Edit recurring invoice"
        title={customerName}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{schedule.currency}</span>
            <InfoHint label="More about fixed fields">
              Customer and currency are fixed for the life of a recurring invoice. To change either,
              cancel this one and create a new one.
            </InfoHint>
            <span aria-hidden>·</span>
            <span>Changes apply to future invoices only</span>
          </span>
        }
        actions={<ScheduleStatusPill status={schedule.status ?? undefined} />}
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Recurrence
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5">
                        First invoice
                        {anchorLocked && <Lock className="size-3 text-muted-foreground" />}
                      </FormLabel>
                      <FormControl>
                        <div
                          className={cn(
                            "rounded-md",
                            anchorLocked && "pointer-events-none opacity-60",
                            !anchorLocked && startDateDirty && DIRTY_RING,
                          )}
                        >
                          <DatePicker
                            value={field.value || null}
                            onChange={(v) => field.onChange(v ?? "")}
                            placeholder="Start date"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cadence"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5">
                        Cadence
                        {anchorLocked && <Lock className="size-3 text-muted-foreground" />}
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={anchorLocked}
                      >
                        <FormControl>
                          <SelectTrigger
                            className={cn(
                              "w-full",
                              !anchorLocked && cadenceDirty && DIRTY_RING,
                            )}
                          >
                            <SelectValue placeholder="Select cadence" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CADENCES.map((c) => (
                            <SelectItem key={c.value} value={c.value}>
                              {c.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="totalOccurrences"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>Occurrences</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={Math.max(1, occurrencesGenerated)}
                          step="1"
                          placeholder="Until cancelled"
                          value={field.value ?? ""}
                          className={cn(fieldState.isDirty && DIRTY_RING)}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === "" ? null : e.target.valueAsNumber,
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {anchorLocked ? (
                <p className="text-xs text-muted-foreground">
                  <Lock className="mr-1 inline size-3" />
                  Start date and cadence are locked once invoices have been generated -
                  they anchor every future invoice date. To change them, pause this
                  recurring invoice and create a new one.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Start date and cadence become locked after the first invoice is
                  generated.
                </p>
              )}
              {occurrencesGenerated > 0 && (
                <p className="text-xs text-muted-foreground">
                  {occurrencesGenerated} {occurrencesGenerated === 1 ? "invoice has" : "invoices have"} already
                  been generated, so occurrences can't be set below that. Setting it equal completes the recurring invoice.
                </p>
              )}

              <FormField
                control={form.control}
                name="paymentTermsDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1">
                      Payment terms (days)
                      <InfoHint label="More about payment terms">
                        Sets the due date on every generated invoice: this many days after each
                        invoice's issue date.
                      </InfoHint>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        max="365"
                        step="1"
                        value={Number.isFinite(field.value) ? field.value : ""}
                        onChange={(e) => field.onChange(e.target.valueAsNumber)}
                        onBlur={(e) => {
                          field.onBlur();
                          if (e.target.value.trim() === "") field.onChange(0);
                        }}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      Applied to future generated invoices. 0 means due on receipt.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="autoSend"
                render={({ field, fieldState }) => (
                  <FormItem
                    className={cn(
                      "flex flex-row items-center justify-between rounded-lg border p-3",
                      fieldState.isDirty && DIRTY_RING,
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel>Send automatically</FormLabel>
                      <p className="text-xs text-muted-foreground">
                        On: each invoice is generated and emailed to the customer in one
                        step.
                        <br />
                        Off: invoices are left as drafts for you to review.
                      </p>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Memo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="memo"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        placeholder="Appears on every generated invoice..."
                        className={cn("resize-none", fieldState.isDirty && DIRTY_RING)}
                        rows={3}
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">
                Line items
              </CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  append({
                    productId: "",
                    quantity: 1,
                    taxRateId: null,
                    unitPriceOverride: null,
                    discountType: "None",
                    discountValue: null,
                  })
                }
              >
                <Plus className="size-4" />
                Add line
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Changes apply to future invoices only - invoices already generated are
                untouched.
              </p>

              {fields.map((field, index) => (
                <ScheduleLineItemRow
                  key={field.id}
                  index={index}
                  control={form.control as unknown as Control<ScheduleLineItemsForm>}
                  products={products}
                  taxRates={taxRates}
                  getProductPrice={getProductPrice}
                  currency={schedule.currency}
                  onRemove={remove}
                  onNewProduct={() => resources.setNewProductOpen(true)}
                  dirtyRing={DIRTY_RING}
                />
              ))}

              {form.formState.errors.lineItems?.root && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.lineItems.root.message}
                </p>
              )}

              <div className="border-t pt-3">
                <DiscountFields
                  control={form.control}
                  typeName="discountType"
                  valueName="discountValue"
                  currency={schedule.currency}
                  label="Invoice discount"
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  Applied to every future invoice, after any per-line discounts. Choose "No discount"
                  to remove it.
                </p>
              </div>
            </CardContent>
          </Card>

          <SchedulePlanSummary
            startDate={watchedStartDate}
            cadence={watchedCadence}
            totalOccurrences={watchedOccurrences}
            autoSend={watchedAutoSend}
            perOccurrence={perOccurrenceSubtotal}
            currency={schedule.currency}
          />

          {saveError && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
              <p className="text-sm text-destructive">{saveError}</p>
            </div>
          )}

          <div className="flex items-center justify-end gap-3">
            {(isDirty || lineItemsDirty || startDateDirty || cadenceDirty) && (
              <span className="text-xs text-muted-foreground">
                Unsaved changes are highlighted
              </span>
            )}
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !(isDirty || lineItemsDirty || startDateDirty || cadenceDirty)}
            >
              {saving ? <Spinner className="size-4" /> : <Save className="size-4" />}
              {saving ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </form>
      </Form>

      <CreateProductDialog
        open={resources.newProductOpen}
        onOpenChange={resources.setNewProductOpen}
        onCreated={resources.refreshProducts}
      />
    </PageContainer>
  );
}
